import { EventResponse, Question } from '../../libs/gds-api-client';
import produce from 'immer';

import { EventType } from './EventItem';
import { Event } from './EventList';

export interface SuccessResponse extends EventResponse {
  deleted?: boolean;
  questions?: Question[];
}

export type AutoFocusableFields = 'date' | 'venue' | 'time';

export const emptyEvent = {
  id: null,
  attire: null,
  requireRsvp: null,
  weddingId: null,
  notes: null,
  name: null,
  type: null,
  date: null,
  time: null,
  endTime: null,
  visible: null,
  location: null,
  subEvents: null,
};

const emptySubEvent = {
  id: null,
  name: null,
  time: null,
  endTime: null,
  type: null,
  attire: null,
  notes: null,
  visible: null,
  location: null,
  __typename: 'GDS_SubEvent' as 'GDS_SubEvent',
};

const emptyLocation = {
  name: null,
  street1: null,
  city: null,
  state: null,
  zip: null,
  country: null,
  __typename: 'GDS_Location' as 'GDS_Location',
};

const emptyOption = {
  text: null,
  description: null,
  __typename: 'GDS_Option' as 'GDS_Option',
};

// Add __typename or apollo cache won't work
export const withTypename = (event: SuccessResponse) => {
  const withTypename = produce<SuccessResponse, EventType, EventType>(event, eventDraft => {
    if (eventDraft.location) {
      // set the fields that are not in the response to null
      eventDraft.location = { ...emptyLocation, ...eventDraft.location };
    }

    if (event.questions) {
      const updatedQuestions = eventDraft.questions?.map(question => {
        if (!question.options) {
          return { ...question, options: null, __typename: 'GDS_Question' as 'GDS_Question' };
        }

        const updatedOptions = question.options?.map(option => ({
          ...emptyOption,
          ...option,
        }));

        return { ...question, __typename: 'GDS_Question' as 'GDS_Question', options: updatedOptions };
      });

      eventDraft.questions = updatedQuestions || null;
    } else {
      eventDraft.questions = null;
    }

    // set the fields that are not in the response to null
    if (eventDraft.subEvents?.length) {
      eventDraft.subEvents = eventDraft.subEvents.map(s => {
        if (s.location) {
          s.location = { ...emptyLocation, ...s.location };
        }

        return { ...emptySubEvent, ...s };
      });
    }

    eventDraft.__typename = 'GDS_Event';

    return eventDraft;
  });

  return { ...emptyEvent, ...withTypename };
};

export const sanitizeEventWithQuestions = (event: Event): Event => {
  const { __typename, ...eventBody } = event;
  let cleanLocation = null,
    cleanQuestions = null,
    cleanSubEvents = null;

  if (eventBody?.location) {
    const { __typename: locationTypename, ...locationBody } = eventBody.location;
    cleanLocation = locationBody;
  }

  if (eventBody?.questions?.length) {
    cleanQuestions = eventBody.questions.map(question => {
      const { __typename, ...questionBody } = question;

      if (questionBody?.options?.length) {
        const cleanOptions = questionBody.options.map(option => {
          const { __typename, ...optionBody } = option;
          return optionBody;
        });
        return { ...questionBody, options: cleanOptions };
      }
      return questionBody;
    });
  }

  if (eventBody?.subEvents?.length) {
    cleanSubEvents = eventBody.subEvents.map(subEvent => {
      const { __typename, ...subEventBody } = subEvent;
      return subEventBody;
    });
  }

  return {
    ...eventBody,
    location: cleanLocation,
    ...(eventBody?.subEvents?.length ? { subEvents: cleanSubEvents } : {}),
    questions: cleanQuestions,
  };
};
